/**
 * Onboarding gate — an authenticated user with no birth data on file gets sent
 * to `/onboarding` instead of being shown screens that cannot compute anything.
 *
 * Without this, a fresh Telegram user landed on Today, Chart and Transits and
 * met three separate "заполните данные рождения" placeholders with no route to
 * actually filling them in.
 *
 * The gate deliberately does NOT fire when we cannot prove who the user is:
 * outside Telegram (`initData === null`) and in mock preview there is no
 * session, so those runs keep their logged-out preview instead of bouncing to
 * a form that has nothing to save against.
 */

/** Routes that must stay reachable while un-onboarded, or the gate self-traps. */
const EXEMPT_PATHS: ReadonlySet<string> = new Set(["/onboarding", "/paywall"]);

export interface GateInput {
  /** Current router pathname. */
  readonly pathname: string;
  /** `onboarded` from `GET /users/me`, or null while unknown/unauthenticated. */
  readonly onboarded: boolean | null;
  /** Whether a Telegram launch context exists (i.e. a session is possible). */
  readonly hasInitData: boolean;
  /** Mock preview runs against fixtures and must never be bounced. */
  readonly mockMode: boolean;
}

/**
 * Pure decision: should this render redirect to `/onboarding`?
 *
 * Fails OPEN — an unknown profile (`onboarded: null`, still loading or the
 * profile call failed) never redirects. A gate that bounced on an error would
 * turn a transient API blip into a redirect loop away from working screens.
 */
export function shouldRedirectToOnboarding(input: GateInput): boolean {
  if (input.mockMode) return false;
  if (!input.hasInitData) return false;
  if (EXEMPT_PATHS.has(input.pathname)) return false;
  return input.onboarded === false;
}
